import './styles/styles.scss';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import countries from 'i18n-iso-countries';
import locale from 'i18n-iso-countries/langs/en.json';
import { keys } from 'lodash';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import React, { Suspense, useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { ErrorBoundary } from 'react-error-boundary';
import { useIdleTimer } from 'react-idle-timer';
import { Route, Routes } from 'react-router';
import { useIntercom } from 'react-use-intercom';

import InviteUser from './auth/InviteUser';
import Login from './auth/Login';
import FinishLogin from './auth/Login/LoginContent/FinishLogin';
import SignUp from './auth/SignUp';
import { useFeatureFlags } from './components/FeatureFlagProvider';
import { LoadingPlaceholder } from './components/LoadingPlaceholder';
import { initDataDog, setRumUser, setUser } from './config/datadog';
import getEnvironment from './config/environment';
import { identify as identifyMixpanel, init } from './config/mixpanel';
import { GlobalErrorFallback } from './GlobalErrorFallback';
import useCurrentClinic from './hooks/useCurrentClinic';
import useLoggedUser from './hooks/useLoggedUser';
import useMixpanel from './hooks/useMixpanel';
import useRTMTracking from './hooks/useRTM/useRTMTracking';
import { useSelectedWorkspaceReconciler } from './hooks/useSelectedWorkspaceReconciler';
import { SetupChatPanelStateProvider } from './hooks/useSetupChatPanelState';
import { useCustomerViewAnalytics, useStaffView } from './hooks/useStaffView';
import useStores from './hooks/useStores';
import { useVisibleColumns } from './hooks/useVisibleColumns';
import AcademyWelcomeView from './Pages/AcademyWelcomeView';
import { NoWorkspaceAccess } from './Pages/NoWorkspaceAccess';
import ReportedEvents from './Pages/PatientDetailsPages/ReportedEvents';
import { getPortalRuntimeConfig } from './services/runtimeConfig';
import { useWebTutorialService } from './services/webTutorialService';
import { getDisplayName } from './utils/nameUtil';

const Clinic = React.lazy(() => import('./Clinic'));
const Layout = React.lazy(() => import('./Layout'));

const cookies = new Cookies();

export const customSelectorIntercomId = 'supportButton';

const profileUnavailableError = new Error('Your profile could not be loaded');
init();
initDataDog();

function App() {
  const { boot } = useIntercom();
  const [isAppReady, setIsAppReady] = useState<boolean>(false);
  const { reconcileSelectedWorkspace } = useSelectedWorkspaceReconciler();
  const { myProfile, isLoading, hasNoWorkspaces, isProfileUnavailable } = useLoggedUser({
    onProfileLoaded: (profile) => reconcileSelectedWorkspace(profile.workspaces),
    redirectsOnSessionEnd: true,
  });
  const { authStore, patientsStore, appStore } = useStores();
  const { track } = useMixpanel();
  const { isStaff } = useStaffView();
  useCustomerViewAnalytics();
  const {
    isLoading: isLoadingClinic,
    hasWorkspaceLoadFailed,
    error: clinicError,
    clinic,
  } = useCurrentClinic();
  const { visibleColumns, getResourcesQueryParam } = useVisibleColumns();
  const { isTutorialListLoading } = useWebTutorialService();
  const { identify } = useFeatureFlags();
  const isAppLoading =
    !isAppReady || isLoading || (!hasNoWorkspaces && isLoadingClinic) || isTutorialListLoading;

  const idleTimeout = 5 * 60 * 1000;
  const { isIdle } = useIdleTimer({
    timeout: idleTimeout,
    crossTab: true,
    onIdle: () => {
      track('user_idle', { idle_timout: idleTimeout });
    },
    onActive: () => {
      track('user_active', { idle_timout: idleTimeout });
      ``;
    },
  });

  useEffect(() => {
    if (visibleColumns && appStore?.clinicUuid) {
      const resources = getResourcesQueryParam();
      patientsStore.setResources(resources);
    }
  }, [visibleColumns, appStore?.clinicUuid]);

  useEffect(() => {
    const load = async () => {
      setIsAppReady(false);

      countries.registerLocale(locale);
      track(`starting clinic with version: ${getPortalRuntimeConfig().appVersion}`);
      setIsAppReady(true);
    };

    if (myProfile?.uuid) {
      load();
      setUser(myProfile);
      identifyMixpanel({
        user: myProfile,
      });

      if (!isStaff) {
        try {
          setRumUser(myProfile, 40);
          track('tracking_rum_user', { user: myProfile.uuid });
        } catch (e) {
          console.error('setRumUser::error', e);
        }
      }
    }
  }, [myProfile?.uuid]);

  useEffect(() => {
    if (clinic?.uuid && myProfile) {
      identify(myProfile.uuid, {
        email: myProfile.email,
        username: myProfile.username,
        fullName: getDisplayName(myProfile),
        isBeta: myProfile.isBeta,
        discipline: myProfile.discipline,
        env: getEnvironment().envName,
        organization: myProfile.org?.name ?? '',
        clinic: clinic.name,
        org_permissions: myProfile?.org?.allowedActions?.join(',') ?? '',
        clinic_permissions: clinic.allowedActions.join(',') ?? '',
      });
    }
  }, [clinic?.uuid, myProfile]);

  useEffect(() => {
    if (getPortalRuntimeConfig().intercom.appId) {
      boot({
        hideDefaultLauncher: true,
        verticalPadding: 84,
        customLauncherSelector: `#${customSelectorIntercomId}`,
      });
    }
  }, [boot]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!isIdle()) {
        if (
          authStore?.authExpiration &&
          moment
            .unix(authStore?.authExpiration ?? 0)
            .subtract(1, 'h')
            .isBefore(moment.now())
        ) {
          authStore.reAuth();
        }
      }
    }, 10 * 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  useRTMTracking();

  useEffect(() => {
    /**
     * @todo clear the removal of the old cookies after a while, and clear related code.
     */
    const all = cookies.getAll();
    const affectCookies = keys(all).filter((x) => x.includes('_clinicVisibleColumns'));

    if (affectCookies.length) {
      affectCookies.forEach((x) => {
        localStorage.setItem(`${x}_old`, JSON.stringify(all[x]));
        cookies.remove(x);
      });
      track(`cleared ${affectCookies.length} cookies`);
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
        <Routes>
          <Route path={'/login'} Component={Login} />
          <Route path={'/finish_login'} Component={FinishLogin} />
          <Route path={'/signup/:inviteKey?'} Component={SignUp} />
          <Route
            path={'/invite_user/:patientUuid'}
            element={<InviteUser isLoading={isAppLoading} />}
          />
          <Route
            path={'/patients/:patientUuid/reported_events'}
            element={<ReportedEvents isLoading={isAppLoading} />}
          />
          <Route path={'/welcome/academy'} element={<AcademyWelcomeView />} />
          <Route
            path={'*'}
            element={
              <SetupChatPanelStateProvider>
                <TooltipPrimitive.Provider>
                  {isProfileUnavailable ? (
                    <GlobalErrorFallback error={profileUnavailableError} />
                  ) : !hasNoWorkspaces && hasWorkspaceLoadFailed ? (
                    <GlobalErrorFallback
                      error={
                        clinicError instanceof Error ? clinicError : new Error(String(clinicError))
                      }
                    />
                  ) : (
                    <LoadingPlaceholder data-cy={'root-loader'} isLoading={isAppLoading}>
                      {hasNoWorkspaces ? (
                        <NoWorkspaceAccess />
                      ) : (
                        <Suspense fallback={<div></div>}>
                          <Layout>
                            <Clinic />
                          </Layout>
                        </Suspense>
                      )}
                    </LoadingPlaceholder>
                  )}
                </TooltipPrimitive.Provider>
              </SetupChatPanelStateProvider>
            }
          />
        </Routes>
      </ErrorBoundary>
    </LocalizationProvider>
  );
}

export default observer(App);
